import {
  FamilyApplicationResp,
  Student,
} from '@/apis/family_application/requests'
import FamilyWizardLayout from '@/app/pages/family_application/FamilyWizardLayout.tsx'
import {
  renewalStudentHasCompletedApplication,
  validateSingleStudent
} from '@/app/pages/family_application/step-checker/student_step_checker.ts'
import { Button, buttonVariants } from '@/common/components/Button'
import { AppRoutePaths } from '@/routing/route_paths'
import {
  RefreshFamilyApplication,
  setSelectedStudent,
} from '@/storage/applications/familyApplicationSlice'
import { ProgramSettingsResp } from '@/storage/program_settings/requests'
import { AppDispatch } from '@/storage/store'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'

type Props = {
  programSettings: ProgramSettingsResp
  application: FamilyApplicationResp
  onRenewStudentClick: (studentId: number) => void
}

export const StudentList = ({
  programSettings,
  application,
  onRenewStudentClick,
}: Props) => {
  const { t: translate } = useTranslation()
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()

  function onStudentRenewClick(studentId: number) {
    onRenewStudentClick(studentId)
  }

  const allStudentsRenewed = application.students.every(
    (student) => student.application_status !== 'Invited'
  )

  function getStatus(student: Student) {
    const status = student.application_status


    if (status === 'Ineligible') {
      return translate('ineligible')
    }

    if (status === 'Withdrawn') {
      return translate('withdrawn')
    }
    if (status === 'Eligible') {
      return translate('renewed')
    }

    if (student.inception_status == 'Family Renewal Applicant') {
      return translate('finish')
    }

    if(student.v1_eligible_for_renewal === false) {
      return translate("ineligible")
    }


    return translate('renew')
  }

  function handleButtonClick(student: Student) {
    if (student.inception_status == 'Family Renewal Applicant') {
      dispatch(RefreshFamilyApplication(programSettings.id))
      dispatch(setSelectedStudent(student.id))

      const validation = validateSingleStudent(student, application)
      navigate(
        validation.urls[0] ??
        AppRoutePaths.family_application.student_details.enter_details
      )
    } else {
      onStudentRenewClick(student.id)
    }
  }

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        <h1>{translate('renew_your_students')}</h1>
      </FamilyWizardLayout.Header>
      <FamilyWizardLayout.Content className="max-w-2xl w-full">
        <p className=" pb-12 text-center font-normal text-gray-800 border-b border-gray-200">
          {translate('students_eligible_renewal')}
        </p>

        <ul className="pt-4">
          {application?.students.map((student, index) => {
            return (
              <li
                key={index}
                className="flex justify-between  w-full  py-3 last:border-b-0 "
              >
                <div className="flex justify-between w-full flex-grow">
                  <div className="w-full flex justify-between space-x-4">
                    <h3 className="font-semibold text-gray-900 flex text-lg items-center">
                      {`${student.first_name ?? ''} ${student.last_name ?? ''}`}
                    </h3>
                    <div>
                      <Button
                        size={'lg'}
                        onClick={() => handleButtonClick(student)}
                        disabled={renewalStudentHasCompletedApplication(
                          application,
                          student
                        )}
                        variant={'primary'}
                      >
                        {getStatus(student)}
                      </Button>
                    </div>
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      </FamilyWizardLayout.Content>
      <FamilyWizardLayout.ActionBar>
        <div className="w-full">
          <Link
            className={`${buttonVariants({
              variant: allStudentsRenewed ? 'primary' : 'outline',
            })} w-full`}
            to={AppRoutePaths.home.index}
          >
            {translate('home')}
          </Link>
        </div>
      </FamilyWizardLayout.ActionBar>
    </FamilyWizardLayout>
  )
}
